<!-- Padding/max-width/centering is owned by AnalyticsPanelComponent's content wrapper (shared
     with the filter bar, matching the design's single DashboardContent root) - this is just the
     content itself. SETTE redesign (CLIPPING-598): token-by-token restyle, no logic change. -->
<div class="space-y-4 body-sm text-ink-muted">
	@if (showSkeleton) {
	<app-analytics-skeleton [kpiCount]="5" [chartCount]="2"></app-analytics-skeleton>
	} @else if (loadError) {
	<app-analytics-error-state [error]="errorState" (retry)="fetchOverview()"></app-analytics-error-state>
	} @else if (isEmptyPeriod) {
	<app-analytics-empty-state variant="no-mentions"></app-analytics-empty-state>
	} @else {
	<div class="space-y-4" [class.opacity-50]="isRefreshing" [class.pointer-events-none]="isRefreshing">

	<!-- ZONE 0: AI summary - 3-4 sentence brief built only from the aggregates below, never raw
	     clip text (see docs/features/analitika/2026-10-08-ai-rezime-overview.md). Hidden entirely
	     while loading/on error, same as the rest of the screen. -->
	@if (!loading && overview?.aiSummary?.insufficientData) {
	<div class="bg-slate-50 rounded-lg border border-slate-200/80 p-3 text-[11px] text-slate-400 flex items-center gap-2">
		<mat-icon class="!w-4 !h-4 !text-base text-slate-300">auto_awesome</mat-icon>
		{{ 'ANALYTICS_OVERVIEW.AI_SUMMARY_INSUFFICIENT' | translate }}
	</div>
	}
	@if (!loading && overview?.aiSummary && !overview!.aiSummary!.insufficientData && aiSummaryFailed) {
	<div class="bg-slate-50 rounded-lg border border-slate-200/80 p-3 flex items-center gap-2">
		<mat-icon class="!w-4 !h-4 !text-base text-slate-300">auto_awesome</mat-icon>
		<p class="text-[11px] text-slate-400 flex-1">{{ 'ANALYTICS_OVERVIEW.AI_SUMMARY_UNAVAILABLE' | translate }}</p>
		<button
			type="button"
			(click)="retryAiSummary()"
			[disabled]="!canRefreshAiSummary || refreshingAiSummary"
			class="text-blue-600 hover:text-blue-700 disabled:text-slate-300 disabled:cursor-not-allowed font-medium text-[11px] shrink-0">
			{{ (refreshingAiSummary ? 'ANALYTICS_OVERVIEW.REFRESHING' : (canRefreshAiSummary ? 'ANALYTICS_STATE.RETRY' : 'ANALYTICS_OVERVIEW.AI_SUMMARY_RETRY_AFTER')) | translate: { time: nextManualRefreshAllowedLabel } }}
		</button>
	</div>
	}
	@if (!loading && overview?.aiSummary && !overview!.aiSummary!.insufficientData && !aiSummaryFailed) {
	<div class="bg-white rounded-lg border border-slate-200/80 shadow-2xs p-4 flex items-start gap-3">
		<mat-icon class="!w-5 !h-5 !text-lg text-blue-500 shrink-0 mt-0.5">auto_awesome</mat-icon>
		<div class="flex-1 min-w-0">
			@if (overview!.aiSummary!.generating) {
			<p class="text-[11px] text-slate-400 italic">{{ 'ANALYTICS_OVERVIEW.AI_SUMMARY_GENERATING' | translate }}</p>
			} @else if (hasStructuredAiSummary) {
			<!-- Structured AI summary (2026-11-01): parsed server-side from the same cached text -
			     see ClientAnalyticsAiSummaryResponse's Javadoc. Falls back to the plain paragraph
			     below whenever structured is null (old cached row, or unparseable model output). -->
			@if (aiSummaryHeadlineSegments.length) {
			<p class="text-xs font-semibold text-slate-800 leading-relaxed mb-2">
				@for (seg of aiSummaryHeadlineSegments; track $index) {
				@if (seg.bold) {<b>{{ seg.text }}</b>} @else {{{ seg.text }}}
				}
			</p>
			}
			<div class="space-y-1.5">
				@if (aiSummaryCoverageSegments.length) {
				<div class="flex items-start gap-1.5 text-xs text-slate-700 leading-relaxed">
					<mat-icon class="!w-3.5 !h-3.5 !text-sm text-slate-400 shrink-0 mt-0.5">article</mat-icon>
					<span>
						<span class="text-slate-400 font-medium">{{ 'ANALYTICS_OVERVIEW.AI_SUMMARY_COVERAGE' | translate }}:</span>
						@for (seg of aiSummaryCoverageSegments; track $index) {
						@if (seg.bold) {<b>{{ seg.text }}</b>} @else {{{ seg.text }}}
						}
					</span>
				</div>
				}
				@if (aiSummaryToneSegments.length) {
				<div class="flex items-start gap-1.5 text-xs text-slate-700 leading-relaxed">
					<mat-icon class="!w-3.5 !h-3.5 !text-sm text-slate-400 shrink-0 mt-0.5">sentiment_satisfied</mat-icon>
					<span>
						<span class="text-slate-400 font-medium">{{ 'ANALYTICS_OVERVIEW.AI_SUMMARY_TONE' | translate }}:</span>
						@for (seg of aiSummaryToneSegments; track $index) {
						@if (seg.bold) {<b>{{ seg.text }}</b>} @else {{{ seg.text }}}
						}
					</span>
				</div>
				}
				@if (aiSummaryStoryViews.length) {
				<div class="flex items-start gap-1.5 text-xs text-slate-700 leading-relaxed">
					<mat-icon class="!w-3.5 !h-3.5 !text-sm text-slate-400 shrink-0 mt-0.5">trending_up</mat-icon>
					<span class="text-slate-400 font-medium shrink-0">{{ 'ANALYTICS_OVERVIEW.AI_SUMMARY_STORIES' | translate }}:</span>
					<div class="flex-1 min-w-0 space-y-1">
						@for (story of aiSummaryStoryViews; track $index) {
						<div class="flex items-start gap-1">
							<mat-icon
								class="!w-3 !h-3 !text-xs shrink-0 mt-0.5"
								[class.text-positive]="story.direction === 'up'"
								[class.text-negative]="story.direction === 'down'">
								{{ story.direction === 'down' ? 'trending_down' : 'trending_up' }}
							</mat-icon>
							<span>
								@for (seg of story.segments; track $index) {
								@if (seg.bold) {<b>{{ seg.text }}</b>} @else {{{ seg.text }}}
								}
							</span>
						</div>
						}
					</div>
				</div>
				}
				@if (aiSummaryCompetitionSegments.length) {
				<div class="flex items-start gap-1.5 text-xs text-slate-700 leading-relaxed">
					<mat-icon class="!w-3.5 !h-3.5 !text-sm text-slate-400 shrink-0 mt-0.5">groups</mat-icon>
					<span>
						<span class="text-slate-400 font-medium">{{ 'ANALYTICS_OVERVIEW.AI_SUMMARY_COMPETITION' | translate }}:</span>
						@for (seg of aiSummaryCompetitionSegments; track $index) {
						@if (seg.bold) {<b>{{ seg.text }}</b>} @else {{{ seg.text }}}
						}
					</span>
				</div>
				}
			</div>
			} @else {
			<p class="text-xs text-slate-700 leading-relaxed">{{ overview!.aiSummary!.text }}</p>
			}
			@if (aiSummaryErrorMessage) {
			<p class="text-[11px] text-red-500 mt-1">{{ aiSummaryErrorMessage }}</p>
			}
			<div class="flex items-center gap-2 mt-2 text-[10px] text-slate-400">
				@if (!overview!.aiSummary!.generating) {
				<span>{{ 'ANALYTICS_OVERVIEW.AI_GENERATED_SUMMARY_LABEL' | translate }} · {{ aiSummaryGeneratedLabel }}</span>
				<app-info-icon key="ANALYTICS_INFO.OVERVIEW.AI_SUMMARY"></app-info-icon>
				}
				<button
					type="button"
					(click)="refreshAiSummary()"
					[disabled]="!canRefreshAiSummary || refreshingAiSummary"
					class="text-blue-600 hover:text-blue-700 disabled:text-slate-300 disabled:cursor-not-allowed font-medium">
					{{ (refreshingAiSummary ? 'ANALYTICS_OVERVIEW.REFRESHING' : 'ANALYTICS_OVERVIEW.REFRESH') | translate }}
				</button>
			</div>
		</div>
	</div>
	}

	<!-- ZONE 1: Stories That Mattered -->
	<div class="bg-surface rounded-2xl border border-line p-4">
		<div class="flex items-center justify-between border-b border-line pb-3 mb-3">
			<div class="flex items-center gap-2">
				<h2 class="section-title">{{ 'ANALYTICS_OVERVIEW.STORIES_TITLE' | translate: { count: sortedStories.length } }}</h2>
				<app-info-icon key="ANALYTICS_INFO.OVERVIEW.TOP_STORIES"></app-info-icon>
				<span class="caption text-ink-subtle font-normal">{{ 'ANALYTICS_OVERVIEW.STORIES_SORTED_NOTE' | translate }}</span>
			</div>
			<div class="caption text-ink-subtle">{{ 'ANALYTICS_OVERVIEW.OF_TOTAL_MENTIONS' | translate: { total: poolMentionsLabel } }}</div>
		</div>

		<div class="space-y-1.5">
			@for (story of sortedStories; track story.id) {
			<div (click)="selectStory(story)" class="group flex items-start sm:items-center justify-between gap-3 p-2.5 rounded-xl border border-line hover:bg-surface-blue transition-colors cursor-pointer">
				<div class="shrink-0 pt-0.5 sm:pt-0">
					<span class="inline-flex items-center justify-center min-w-[54px] px-2 py-1 rounded-lg text-xs font-medium tabular-nums" [class]="toneClasses(impactTone(story.impact))">
						{{ impactLabel(story.impact) }}
					</span>
				</div>

				<div class="flex-1 min-w-0">
					<h3 class="text-[13px] font-medium text-ink group-hover:text-blue transition-colors leading-snug line-clamp-2 sm:line-clamp-1">{{ story.headline }}</h3>
					<div class="flex flex-wrap items-center gap-x-2 gap-y-0.5 caption text-ink-subtle font-normal mt-0.5">
						@if (story.outlet) {
						<span class="font-medium text-ink-muted">{{ story.outlet }}</span>
						}
						@if (story.outlet && (story.reach != null || story.dateLabel)) {
						<span>·</span>
						}
						@if (story.reach != null) {
						<span>{{ 'ANALYTICS_OVERVIEW.VISIBILITY_SLASH_100' | translate: { value: story.reach } }}</span>
						}
						@if (story.reach != null && story.dateLabel) {
						<span>·</span>
						}
						@if (story.dateLabel) {
						<span>{{ story.dateLabel }}</span>
						}
						@for (topic of story.topics; track topic) {
						<span class="bg-surface-gray text-ink-muted px-1.5 py-0.2 rounded text-[10px]">{{ translateStoryTopic(topic) }}</span>
						}
						@if (story.competitorOnly) {
						<span class="bg-surface-gray text-ink-muted px-1.5 py-0.2 rounded text-[10px] font-medium" [title]="'CLIPS.COMPETITOR_TOOLTIP' | translate">{{ 'CLIPS.COMPETITOR_BADGE' | translate }}</span>
						}
					</div>
				</div>

				<div class="shrink-0 text-ink-subtle group-hover:text-blue transition-colors pt-0.5 sm:pt-0">
					<mat-icon class="!w-4 !h-4 !text-base">chevron_right</mat-icon>
				</div>
			</div>
			}
			@if (!loading && sortedStories.length === 0) {
			<div class="caption text-ink-subtle text-center py-4">{{ 'ANALYTICS_OVERVIEW.NO_SENTIMENT_STORIES' | translate }}</div>
			}
		</div>
	</div>

	<!-- ZONE 2: How We're Doing -->
	<div class="space-y-3.5">
		<div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
			<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.MENTIONS' | translate" infoKey="ANALYTICS_INFO.COMMON.MENTIONS" [value]="mentionsKpi.value" [trendUp]="mentionsKpi.trendUp" [deltaLabel]="mentionsKpi.deltaLabel" [rangeText]="mentionsKpi.rangeText" [subLabel]="compareSubLabel" [sampleSize]="overview?.mentions?.sampleSize"></app-kpi-tile>
			<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.NET_SENTIMENT' | translate" infoKey="ANALYTICS_INFO.COMMON.NET_SENTIMENT" [value]="netSentimentKpi.value" [valueTone]="netSentimentValueTone" [trendUp]="netSentimentKpi.trendUp" [deltaLabel]="netSentimentKpi.deltaLabel" [rangeText]="netSentimentKpi.rangeText" [subLabel]="'· ' + ('ANALYTICS_OVERVIEW.NET_SENTIMENT_SUBLABEL' | translate)" [sampleSize]="overview?.netSentiment?.sampleSize"></app-kpi-tile>
			<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.AVG_VISIBILITY_INDEX' | translate" infoKey="ANALYTICS_INFO.COMMON.VISIBILITY" [value]="avgReachKpi.value" [trendUp]="avgReachKpi.trendUp" [deltaLabel]="avgReachKpi.deltaLabel" [rangeText]="avgReachKpi.rangeText" [subLabel]="'· ' + ('ANALYTICS_GLOSSARY.AVG_VISIBILITY_SUBLABEL' | translate)" [sampleSize]="overview?.avgReach?.sampleSize"></app-kpi-tile>
			<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.AVG_IMPACT_SCORE' | translate" infoKey="ANALYTICS_INFO.COMMON.IMPACT" [value]="avgImpactKpi.value" [trendUp]="avgImpactKpi.trendUp" [deltaLabel]="avgImpactKpi.deltaLabel" [rangeText]="avgImpactKpi.rangeText" [subLabel]="'· ' + ('ANALYTICS_GLOSSARY.AVG_IMPACT_SUBLABEL' | translate)" [sampleSize]="overview?.avgImpact?.sampleSize"></app-kpi-tile>
			<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.SHARE_OF_VOICE' | translate" infoKey="ANALYTICS_INFO.COMMON.SHARE_OF_VOICE" [value]="shareOfVoiceKpi.value" [trendUp]="shareOfVoiceKpi.trendUp" [deltaLabel]="shareOfVoiceKpi.deltaLabel" [rangeText]="shareOfVoiceKpi.rangeText" [subLabel]="shareOfVoiceSubLabel" [sampleSize]="overview?.shareOfVoice?.sampleSize"></app-kpi-tile>
		</div>

		<div class="grid grid-cols-1 lg:grid-cols-3 gap-3.5">
			<!-- LEFT: Trend chart (2/3 width) -->
			<div class="lg:col-span-2 bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between">
				<div>
					<div class="flex flex-wrap items-center justify-between gap-2 mb-1">
						<div>
							<h3 class="section-title flex items-center gap-1">{{ 'ANALYTICS_OVERVIEW.TREND_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.OVERVIEW.TREND"></app-info-icon></h3>
							<p class="caption text-ink-subtle font-normal mt-0.5">{{ 'ANALYTICS_OVERVIEW.TREND_SUBTITLE' | translate: { grouping: (isWeeklyBucketing ? 'ANALYTICS_OVERVIEW.GROUPING_WEEKLY' : 'ANALYTICS_OVERVIEW.GROUPING_DAILY') | translate, range: filters.dateRange } }}</p>
						</div>
					</div>

					<!-- Hand-built legend (same as before - Chart.js's own legend is disabled in
					     trendChartOptions so this is the only one rendered) -->
					<div class="flex flex-wrap items-center gap-4 caption text-ink-muted font-normal mt-2 mb-3 pt-2 border-t border-line">
						<div class="flex items-center gap-1.5">
							<span class="w-3 h-3 rounded-[3px]" style="background-color: #D7DBE3"></span>
							<span class="font-medium text-ink">{{ 'ANALYTICS_OVERVIEW.LEGEND_MENTIONS_BARS' | translate }}</span>
						</div>
						<div class="flex items-center gap-1.5">
							<span class="w-3 h-0.5" style="background-color: #F59E0B"></span>
							<span class="font-medium text-ink">{{ 'ANALYTICS_OVERVIEW.LEGEND_AVG_VISIBILITY' | translate }}</span>
						</div>
						<div class="flex items-center gap-1.5">
							<span class="w-3 h-0.5 bg-blue"></span>
							<span class="font-medium text-ink">{{ 'ANALYTICS_OVERVIEW.LEGEND_AVG_IMPACT' | translate }}</span>
						</div>
					</div>

					<app-chart type="bar" [data]="trendChartData" [options]="trendChartOptions" height="256px"></app-chart>
				</div>

				<div class="caption text-ink-subtle font-normal mt-2 text-right">{{ 'ANALYTICS_OVERVIEW.TOTAL_MENTIONS_ACROSS' | translate: { total: totalMentionsLabel, buckets: bucketCount, unit: (trendBucketUnit | translate), avg: avgMentionsPerBucket, perUnit: (isWeeklyBucketing ? 'ANALYTICS_OVERVIEW.PER_WEEK' : 'ANALYTICS_OVERVIEW.PER_DAY') | translate } }}</div>
			</div>

			<!-- RIGHT: Competitor list (1/3 width, text list not a donut) -->
			<div class="lg:col-span-1 bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between">
				<div>
					<div class="flex items-center justify-between border-b border-line pb-2.5 mb-3">
						<div>
							<h3 class="section-title flex items-center gap-1">{{ 'ANALYTICS_OVERVIEW.COMPETITOR_LIST_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.OVERVIEW.COMPETITOR_LIST"></app-info-icon></h3>
							<p class="caption text-ink-subtle font-normal mt-0.5">{{ 'ANALYTICS_OVERVIEW.COMPETITOR_LIST_SUBTITLE' | translate }}</p>
						</div>
						<span class="caption text-ink-subtle bg-surface-gray px-2 py-0.5 rounded-full border border-line">{{ 'ANALYTICS_OVERVIEW.TOTAL_SUFFIX' | translate: { total: poolMentionsLabel } }}</span>
					</div>

					<div class="space-y-2.5">
						@for (brand of competitorList; track brand.tagId) {
						<div class="p-2 rounded-xl transition-colors" [class]="brand.isClient ? 'bg-blue-tint' : 'hover:bg-surface-gray'">
							<div class="flex items-center justify-between text-xs mb-1">
								<div class="flex items-center gap-1.5 font-medium text-ink">
									<span class="w-2 h-2 rounded-full" [style.background-color]="brand.colorHex"></span>
									<span>{{ brand.name }}</span>
									@if (brand.isClient) {
									<span class="text-[9px] bg-blue text-on-blue px-1 py-0.2 rounded-full font-medium">{{ 'ANALYTICS_GLOSSARY.CLIENT' | translate }}</span>
									}
								</div>
								<div class="flex items-center gap-2">
									<span class="caption text-ink-subtle font-normal">{{ 'ANALYTICS_OVERVIEW.MENTIONS_SUFFIX' | translate: { count: brand.mentionsLabel } }}</span>
									<span class="text-xs font-medium text-ink w-12 text-right tabular-nums">{{ brand.sovLabel }}</span>
								</div>
							</div>
							<div class="w-full bg-neutral-tint rounded-full h-1.5 overflow-hidden">
								<div class="h-full rounded-full" [style.background-color]="brand.colorHex" [style.width.%]="(brand.sov / maxSov) * 100"></div>
							</div>
						</div>
						}
						@if (!loading && competitorList.length === 0) {
						<div class="caption text-ink-subtle text-center py-4">{{ 'ANALYTICS_OVERVIEW.NO_BRAND_TAGS' | translate }}</div>
						}
					</div>
				</div>
			</div>
		</div>
	</div>

	<!-- ZONE 3: What Changed - real movement cards (brand mentions/SOV swings, topic-impact
	     swings, medium silence detection). The first three need a compare period; silence
	     detection doesn't, since it looks at the brand's own mention history independently. -->
	<div class="bg-surface rounded-2xl border border-line p-4">
		<div class="flex items-center justify-between border-b border-line pb-3 mb-3">
			<div>
				<h2 class="section-title flex items-center gap-1">{{ 'ANALYTICS_OVERVIEW.MOVEMENTS_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.OVERVIEW.MOVEMENTS"></app-info-icon></h2>
				<p class="caption text-ink-subtle font-normal mt-0.5">{{ 'ANALYTICS_OVERVIEW.MOVEMENTS_SUBTITLE' | translate }}</p>
			</div>
			<span class="caption bg-blue-tint text-blue px-2 py-0.5 rounded-full">{{ 'ANALYTICS_OVERVIEW.MOVEMENT_SIGNALS_BADGE' | translate }}</span>
		</div>

		@if (movements.length) {
		<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
			@for (card of movements; track card.headline) {
			<div (click)="onMovementClick(card)" class="group flex items-start gap-2.5 p-2.5 rounded-xl border border-line hover:bg-surface-blue transition-colors cursor-pointer">
				<div class="shrink-0 pt-0.5">
					<span class="inline-flex items-center justify-center min-w-[58px] px-2 py-1 rounded-lg text-xs font-medium tabular-nums" [class]="movementBadgeClasses(movementBadgeType(card))">{{ card.badge }}</span>
				</div>
				<div class="flex-1 min-w-0">
					<h4 class="text-xs font-medium text-ink group-hover:text-blue transition-colors leading-snug">{{ card.headline }}</h4>
					<p class="caption text-ink-subtle font-normal mt-0.5 line-clamp-1">{{ card.details }}</p>
				</div>
				<div class="shrink-0 text-ink-subtle group-hover:text-blue transition-colors pt-0.5">
					<mat-icon class="!w-3.5 !h-3.5 !text-sm">arrow_forward</mat-icon>
				</div>
			</div>
			}
		</div>
		} @else if (!loading) {
		<p class="caption text-ink-subtle text-center py-4">
			{{ (hasComparePeriod ? 'ANALYTICS_OVERVIEW.NO_MOVEMENTS_WITH_COMPARE' : 'ANALYTICS_OVERVIEW.NO_MOVEMENTS_NO_COMPARE') | translate }}
		</p>
		}
	</div>

	<!-- BOTTOM: Direct links to detail pages -->
	<div class="space-y-2 pt-1">
		<div class="overline-label text-ink-subtle">{{ 'ANALYTICS_OVERVIEW.DEEP_DIVE_DASHBOARDS' | translate }}</div>
		<div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
			@for (page of detailPages; track page.id) {
			<button
				type="button"
				(click)="onDeepDiveClick(page)"
				class="group flex flex-col justify-between text-left p-3 rounded-2xl bg-surface border border-line hover:border-blue transition-colors cursor-pointer">
				<div>
					<div class="flex items-center justify-between mb-1.5">
						<div class="p-1.5 rounded-lg bg-surface-gray text-ink-muted group-hover:bg-blue-tint group-hover:text-blue transition-colors">
							<mat-icon class="!w-4 !h-4 !text-base">{{ page.icon }}</mat-icon>
						</div>
						<mat-icon class="!w-3.5 !h-3.5 !text-sm text-ink-subtle group-hover:text-blue transition-colors">arrow_forward</mat-icon>
					</div>
					<div class="font-medium text-ink text-xs group-hover:text-blue transition-colors">{{ page.label }}</div>
					<div class="caption text-ink-subtle font-normal mt-0.5 line-clamp-1">{{ page.desc }}</div>
				</div>
			</button>
			}
		</div>
	</div>

	<!-- FOOTNOTE -->
	<div class="pt-3 border-t border-line pb-2 caption text-ink-subtle font-normal leading-relaxed text-center sm:text-left">
		{{ 'ANALYTICS_OVERVIEW.FOOTNOTE' | translate }}
	</div>
	</div>
	}

	<!-- ARTICLE CLIPPING MODAL -->
	@if (selectedStory) {
	<div class="fixed inset-0 z-50 bg-ink/60 backdrop-blur-xs flex items-center justify-center p-4">
		<div class="bg-surface rounded-2xl shadow-pop border border-line w-full max-w-xl p-6 relative text-ink space-y-4">
			<div class="flex items-start justify-between border-b border-line pb-3">
				<div>
					<div class="caption text-ink-subtle font-normal">{{ storyMeta(selectedStory) }}</div>
					<h3 class="item-title text-ink mt-1">{{ selectedStory.headline }}</h3>
				</div>
				<button type="button" (click)="closeModal()" class="text-ink-subtle hover:text-ink p-1 rounded-lg hover:bg-surface-gray transition-colors">
					<mat-icon class="!w-5 !h-5 !text-xl">close</mat-icon>
				</button>
			</div>

			<div class="grid grid-cols-4 gap-2 bg-surface-gray p-3 rounded-xl text-xs">
				<div>
					<span class="caption text-ink-subtle block font-normal">{{ 'ANALYTICS_OVERVIEW.MODAL_BRAND' | translate }}</span>
					<span class="font-medium text-blue">{{ selectedStory.brand || '—' }}</span>
					@if (selectedStory.competitorOnly) {
					<span class="ml-1 bg-surface-gray text-ink-muted px-1.5 py-0.2 rounded text-[10px] font-medium" [title]="'CLIPS.COMPETITOR_TOOLTIP' | translate">{{ 'CLIPS.COMPETITOR_BADGE' | translate }}</span>
					}
				</div>
				<div>
					<span class="caption text-ink-subtle block font-normal">{{ 'ANALYTICS_GLOSSARY.VISIBILITY_INDEX' | translate }}</span>
					<span class="font-medium text-ink">{{ selectedStory.reach != null ? selectedStory.reach + '/100' : '—' }}</span>
				</div>
				<div>
					<span class="caption text-ink-subtle block font-normal">{{ 'ANALYTICS_GLOSSARY.IMPACT' | translate }}</span>
					<span class="font-medium" [class]="toneTextClass(impactTone(selectedStory.impact))">
						{{ impactLabel(selectedStory.impact) }}
					</span>
				</div>
				<div>
					<span class="caption text-ink-subtle block font-normal">{{ 'ANALYTICS_GLOSSARY.SENTIMENT' | translate }}</span>
					<app-sentiment-badge [sentiment]="selectedStory.sentiment"></app-sentiment-badge>
				</div>
			</div>

			@if (selectedStory.topics.length) {
			<div class="flex flex-wrap items-center gap-1.5">
				@for (topic of selectedStory.topics; track topic) {
				<span class="bg-surface-gray text-ink-muted px-1.5 py-0.5 rounded text-[10px] font-medium">{{ translateStoryTopic(topic) }}</span>
				}
			</div>
			}

			@if (selectedStory.brief) {
			<div>
				<h4 class="text-xs font-medium text-ink-muted mb-1">{{ 'ANALYTICS_OVERVIEW.MODAL_BRIEF' | translate }}</h4>
				<p class="text-xs text-ink-muted bg-surface-gray p-3 rounded-xl border border-line leading-relaxed italic">"{{ selectedStory.brief }}"</p>
			</div>
			}

			@if (selectedStory.authors || selectedStory.outletType) {
			<div class="flex items-center justify-between text-xs text-ink-subtle pt-2 border-t border-line">
				@if (selectedStory.authors) {
				<div><span>{{ 'ANALYTICS_OVERVIEW.MODAL_JOURNALIST_AUTHOR' | translate }}: </span><strong class="text-ink font-medium">{{ selectedStory.authors }}</strong></div>
				}
				@if (selectedStory.outletType) {
				<div><span>{{ 'ANALYTICS_OVERVIEW.MODAL_CHANNEL' | translate }}: </span><strong class="text-ink font-medium">{{ selectedStory.outletType }}</strong></div>
				}
			</div>
			}

			<div class="flex items-center justify-end gap-2 pt-2">
				<button type="button" (click)="closeModal()" class="h-10 px-[18px] rounded-full border border-line text-ink text-[15px] font-medium hover:bg-surface-gray transition-colors">{{ 'BUTTON.CLOSE' | translate }}</button>
				@if (selectedStory.url) {
				<a [href]="selectedStory.url" target="_blank" rel="noopener" class="h-10 px-[18px] inline-flex items-center gap-1.5 bg-blue text-on-blue rounded-full text-[15px] font-medium hover:bg-blue-hover transition-colors">
					<span>{{ 'ANALYTICS_OVERVIEW.VIEW_FULL_CLIPPING' | translate }}</span>
					<mat-icon class="!w-3.5 !h-3.5 !text-sm">open_in_new</mat-icon>
				</a>
				}
			</div>
		</div>
	</div>
	}
</div>
